import { format } from 'date-fns';

import {
  makeCheckpointKey,
  normalizeCheckpointId,
  normalizeCheckpointVersion,
} from '@/lib/checkpointKeys';
import type {
  Checkpoint,
  CheckpointSystemStatus,
  SystemCheckpointConfiguration,
} from '@/types';

import type {
  CheckpointRow,
  MaintenanceCriticality,
} from './CheckpointSettingsPage.types';

export const MAINTENANCE_CRITICALITY_OPTIONS: Array<{
  value: MaintenanceCriticality;
  label: string;
  days: number;
}> = [
  { value: 'CRITICAL', label: 'Critical - 24 hours', days: 1 },
  { value: 'HIGH', label: 'High - 3 days', days: 3 },
  { value: 'MEDIUM', label: 'Medium - 7 days', days: 7 },
];

export const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toLocalDateTimeInput = (value?: string | Date | null) => {
  const parsed = toDate(value);
  if (!parsed) return '';
  return format(parsed, "yyyy-MM-dd'T'HH:mm");
};

export const getCurrentLocalDateTimeInput = () => {
  return toLocalDateTimeInput(new Date());
};

export const isPastDateTimeInput = (value?: string) => {
  const parsed = toDate(value);
  if (!parsed) return false;

  const now = new Date();
  now.setSeconds(0, 0);
  return parsed.getTime() < now.getTime();
};

export const isEndDateNear = (value?: string | null, days = 7) => {
  const parsed = toDate(value);
  if (!parsed) return false;
  const diff = parsed.getTime() - Date.now();
  return diff > 0 && diff <= days * 24 * 60 * 60 * 1000;
};

export const isUnscoredWindowExpired = (window: unknown) => {
  if (!window || typeof window !== 'object') return false;
  const record = window as Record<string, unknown>;
  const endRaw = (record.endDate ?? record.EndDate ?? '').toString().trim();
  if (!endRaw) return false;
  const endMs = new Date(endRaw).getTime();
  return !Number.isNaN(endMs) && endMs <= Date.now();
};

export const isTransitionAllowed = (
  from: CheckpointSystemStatus,
  to: CheckpointSystemStatus,
) => {
  if (from === to) return false;
  if (to === 'MAINTENANCE') return from === 'LIVE';
  if (from === 'MAINTENANCE' && to === 'PREVIEW') return false;

  return true;
};

export const CHECKPOINT_STATUS_MAP: Record<
  string,
  { label: string; color: string }
> = {
  LIVE: {
    label: 'LIVE',
    color: 'green',
  },
  PREVIEW: {
    label: 'PREVIEW',
    color: 'orange',
  },
  MAINTENANCE: {
    label: 'MAINTENANCE',
    color: 'red',
  },
  DISABLED: {
    label: 'DISABLED',
    color: 'gray',
  },
};

export const normalizeCheckpointStatus = (
  checkpoint: Checkpoint,
  config?: SystemCheckpointConfiguration,
): CheckpointSystemStatus => {
  if (checkpoint.enabled === false) return 'DISABLED';

  const hasActiveWindow = (value: unknown) => {
    if (value === true) return true;
    if (!value || typeof value !== 'object') return false;

    const record = value as Record<string, unknown>;
    if (typeof record.enabled === 'boolean') return record.enabled;

    const fields = [
      'reason',
      'startDate',
      'endDate',
      'type',
      'unscoredType',
      'unscored_type',
    ];

    return fields.some((field) => {
      const current = record[field];
      if (typeof current === 'string') return current.trim().length > 0;
      return current !== null && current !== undefined;
    });
  };

  const statusField = config?.status;
  if (statusField && typeof statusField === 'object') {
    const statusObj = statusField as Record<string, unknown>;
    const disabled = statusObj.disabled;
    const unScored = (statusObj.unScored ?? statusObj.unscored) as
      | Record<string, unknown>
      | undefined;

    if (hasActiveWindow(unScored)) {
      const typeRaw = (
        unScored?.type ??
        unScored?.unscoredType ??
        unScored?.unscored_type ??
        ''
      )
        .toString()
        .trim()
        .toLowerCase();

      if (typeRaw) {
        if (typeRaw === 'maintenance') {
          const endDateRaw = (unScored?.endDate ?? unScored?.EndDate ?? '')
            .toString()
            .trim();
          const endMs = endDateRaw ? new Date(endDateRaw).getTime() : Number.NaN;

          if (
            !Number.isNaN(endMs) &&
            endMs <= Date.now() &&
            hasActiveWindow(disabled)
          ) {
            return 'DISABLED';
          }

          return 'MAINTENANCE';
        }

        return isUnscoredWindowExpired(unScored) ? 'LIVE' : 'PREVIEW';
      }

      const reasonRaw = (unScored?.reason ?? unScored?.Reason ?? '')
        .toString()
        .trim()
        .toLowerCase();
      if (reasonRaw) {
        if (reasonRaw.includes('maintenance')) {
          const endDateRaw = (unScored?.endDate ?? unScored?.EndDate ?? '')
            .toString()
            .trim();
          const endMs = endDateRaw ? new Date(endDateRaw).getTime() : Number.NaN;

          if (
            !Number.isNaN(endMs) &&
            endMs <= Date.now() &&
            hasActiveWindow(disabled)
          ) {
            return 'DISABLED';
          }

          return 'MAINTENANCE';
        }

        return isUnscoredWindowExpired(unScored) ? 'LIVE' : 'PREVIEW';
      }
    }

    if (hasActiveWindow(disabled)) {
      return 'DISABLED';
    }
  }

  const raw = (config?.state ?? config?.mode ?? '')
    .toString()
    .trim()
    .toUpperCase();

  switch (raw) {
    case 'PREVIEW':
      return 'PREVIEW';
    case 'MAINTENANCE':
      return 'MAINTENANCE';
    case 'DISABLED':
      return 'DISABLED';
    default:
      return 'LIVE';
  }
};

export const isScoredFromStatus = (status: CheckpointSystemStatus) => {
  return status === 'LIVE';
};

export const formatStatusDate = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd HH:mm');
};

export const getStatusTooltipLines = (row?: CheckpointRow | null) => {
  if (!row) return [];
  const status = (row.status ?? 'LIVE').toString();
  if (status === 'LIVE') return [];

  const tooltip = row.statusTooltip;

  const cleanReason = (value?: string) =>
    (value ?? '')
      .toString()
      .replace(/-\s*maintenance\s*$/i, '')
      .trim();

  const reasonText = cleanReason(tooltip?.reason);
  const startText = formatStatusDate(tooltip?.startDate);
  const endText = formatStatusDate(tooltip?.endDate);

  const lines: string[] = [];
  if (reasonText) lines.push(`Reason: ${reasonText}`);
  if (startText && startText !== endText) lines.push(`Start: ${startText}`);
  if (endText) lines.push(`End: ${endText}`);
  return lines;
};

const pickArray = <T,>(payload: unknown): T[] => {
  const source = payload as Record<string, unknown> | undefined;
  const candidates = [
    payload,
    source?.data,
    (source?.data as Record<string, unknown> | undefined)?.data,
    source?.items,
    (source?.data as Record<string, unknown> | undefined)?.items,
    source?.checkpoints,
    (source?.data as Record<string, unknown> | undefined)?.checkpoints,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[];
  }

  return [];
};

const getConfigCheckpointId = (cfg: SystemCheckpointConfiguration) => {
  const anyCfg = cfg as Record<string, unknown>;
  return (
    anyCfg?.checkpointId ??
    anyCfg?.checkpointID ??
    anyCfg?.checkpoint_id ??
    anyCfg?.id ??
    ''
  )
    .toString()
    .trim();
};

const getConfigCheckpointVersion = (cfg: SystemCheckpointConfiguration) => {
  const anyCfg = cfg as Record<string, unknown>;
  return (
    anyCfg?.checkpointVersion ??
    anyCfg?.checkpoint_version ??
    anyCfg?.version ??
    anyCfg?.Version ??
    null
  );
};

export const buildCheckpointRows = (
  checkpointsData: unknown,
  checkpointConfigsData: unknown,
) => {
  if (!checkpointsData || !checkpointConfigsData) return [];

  const configs = pickArray<SystemCheckpointConfiguration>(
    (checkpointConfigsData as Record<string, unknown> | undefined)?.data,
  );

  const configByCheckpointKey = new Map(
    (configs ?? [])
      .map((cfg) => {
        const id = getConfigCheckpointId(cfg);
        const version = getConfigCheckpointVersion(cfg);
        return [makeCheckpointKey(id, version), cfg] as const;
      })
      .filter(([key]) => Boolean(key.split('::')[0])),
  );

  const configByCheckpointIdOnly = new Map(
    (configs ?? [])
      .filter(
        (cfg) =>
          normalizeCheckpointVersion(getConfigCheckpointVersion(cfg)) === '',
      )
      .map((cfg) => [normalizeCheckpointId(getConfigCheckpointId(cfg)), cfg] as const)
      .filter(([id]) => Boolean(id)),
  );

  const checkpointsRaw = pickArray<Checkpoint>(
    (checkpointsData as Record<string, unknown> | undefined)?.data,
  );

  return checkpointsRaw
    .slice()
    .map((checkpoint) => {
      const checkpointRecord = checkpoint as unknown as Record<string, unknown>;
      const checkpointId =
        checkpointRecord?.id ??
        checkpointRecord?.checkpointId ??
        checkpointRecord?.checkpointID ??
        checkpointRecord?.checkpoint_id ??
        '';

      const checkpointVersion =
        checkpointRecord?.version ??
        checkpointRecord?.checkpointVersion ??
        checkpointRecord?.checkpoint_version ??
        checkpointRecord?.Version ??
        null;

      const cfg = (() => {
        const hasVersion =
          checkpointVersion !== null &&
          checkpointVersion !== undefined &&
          `${checkpointVersion}`.trim() !== '';

        if (hasVersion) {
          return configByCheckpointKey.get(
            makeCheckpointKey(checkpointId, checkpointVersion),
          );
        }

        return configByCheckpointIdOnly.get(normalizeCheckpointId(checkpointId));
      })();

      const status = normalizeCheckpointStatus(checkpoint, cfg);

      const statusTooltip = (() => {
        const statusObj = (cfg as Record<string, unknown> | undefined)?.status;
        if (!statusObj || typeof statusObj !== 'object') return undefined;

        const statusRecord = statusObj as Record<string, unknown>;
        const disabled = statusRecord.disabled as Record<string, unknown> | undefined;
        const unScored = (statusRecord.unScored ?? statusRecord.unscored) as
          | Record<string, unknown>
          | undefined;

        const read = (window: Record<string, unknown> | undefined) => {
          if (!window || typeof window !== 'object') return {};
          return {
            reason: window.reason ?? window.note,
            startDate: window.startDate,
            endDate: window.endDate,
          } as { reason?: string; startDate?: string; endDate?: string };
        };

        if (status === 'DISABLED') {
          return read(disabled);
        }

        if (status === 'PREVIEW') {
          return read(unScored);
        }

        if (status === 'MAINTENANCE') {
          const unscoredRead = read(unScored);
          const disabledRead = read(disabled);
          return {
            reason: unscoredRead.reason,
            startDate: unscoredRead.startDate ?? disabledRead.startDate,
            endDate: unscoredRead.endDate,
          };
        }

        return undefined;
      })();

      const cfgViews = cfg?.views
        ?.map((v) => `${v}`.trim().toLowerCase())
        .filter(Boolean);

      return {
        ...checkpoint,
        id: checkpointId?.toString?.() ? checkpointId.toString() : checkpointId,
        version:
          checkpointVersion === null || checkpointVersion === undefined
            ? undefined
            : checkpointVersion,
        isScored: isScoredFromStatus(status),
        status,
        views: cfgViews,
        statusTooltip,
      } as CheckpointRow;
    })
    .sort((left, right) => {
      const byName = left.displayName.localeCompare(right.displayName);
      if (byName !== 0) return byName;
      return `${left.version ?? ''}`.localeCompare(`${right.version ?? ''}`);
    });
};
